import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { PlusIcon, PencilIcon, TrashIcon, XIcon, SearchIcon, ImageIcon } from 'lucide-react';
import { useStore } from '../../context/StoreContext';
import { Product, Size } from '../../data/products';
const CATEGORIES = ['Shirts', 'T-Shirts', 'Jeans', 'Jackets', 'Hoodies'];
const SIZES: Size[] = ['S', 'M', 'L', 'XL', 'XXL'];
interface ProductForm {
  name: string;
  description: string;
  category: string;
  subcategory: string;
  images: string;
  mrp: string;
  sellingPrice: string;
  sizes: Record<Size, string>;
  tags: string;
  isFeatured: boolean;
  isNewArrival: boolean;
}
const emptyForm: ProductForm = {
  name: '',
  description: '',
  category: 'Shirts',
  subcategory: '',
  images: '',
  mrp: '',
  sellingPrice: '',
  sizes: {
    S: '0',
    M: '0',
    L: '0',
    XL: '0',
    XXL: '0'
  },
  tags: '',
  isFeatured: false,
  isNewArrival: false
};
export function AdminProducts() {
  const {
    products,
    addProduct,
    editProduct,
    deleteProduct
  } = useStore();
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ProductForm>(emptyForm);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const filtered = products.filter(p => p.name.toLowerCase().includes(search.toLowerCase()) || p.category.toLowerCase().includes(search.toLowerCase()));
  const openAddForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(true);
  };
  const openEditForm = (product: Product) => {
    setForm({
      name: product.name,
      description: product.description,
      category: product.category,
      subcategory: product.subcategory,
      images: product.images.join(', '),
      mrp: String(product.mrp),
      sellingPrice: String(product.sellingPrice),
      sizes: Object.fromEntries(SIZES.map(s => [s, String(product.sizes[s])])) as Record<Size, string>,
      tags: product.tags.join(', '),
      isFeatured: product.isFeatured,
      isNewArrival: product.isNewArrival
    });
    setEditingId(product.id);
    setShowForm(true);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const productData: Product = {
      id: editingId || `p_${Date.now()}`,
      name: form.name,
      description: form.description,
      category: form.category,
      subcategory: form.subcategory,
      images: form.images.split(',').map(s => s.trim()).filter(Boolean),
      mrp: Number(form.mrp) || 0,
      sellingPrice: Number(form.sellingPrice) || 0,
      sizes: Object.fromEntries(SIZES.map(s => [s, Number(form.sizes[s]) || 0])) as Record<Size, number>,
      ratings: 4.0,
      reviewCount: 0,
      reviews: [],
      tags: form.tags.split(',').map(s => s.trim()).filter(Boolean),
      isFeatured: form.isFeatured,
      isNewArrival: form.isNewArrival
    };
    if (editingId) {
      const existing = products.find(p => p.id === editingId);
      if (existing) {
        productData.ratings = existing.ratings;
        productData.reviewCount = existing.reviewCount;
        productData.reviews = existing.reviews;
      }
      editProduct(productData);
    } else {
      addProduct(productData);
    }
    setShowForm(false);
  };
  const getTotalStock = (product: Product) => Object.values(product.sizes).reduce((a, b) => a + b, 0);
  return <div className="space-y-6" data-id="element-565">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-566">
        <div className="relative flex-1 max-w-md" data-id="element-567">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-568" />
          <input type="text" placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" data-id="element-569" />
        </div>
        <button onClick={openAddForm} className="flex items-center gap-2 px-4 py-2.5 bg-sky-600 text-white text-sm font-medium rounded-lg hover:bg-sky-700 transition-colors" data-id="element-570">
          <PlusIcon className="w-4 h-4" data-id="element-571" /> Add Product
        </button>
      </div>

      {/* Products Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-572">
        <div className="overflow-x-auto" data-id="element-573">
          <table className="w-full text-sm" data-id="element-574">
            <thead data-id="element-575">
              <tr className="border-b border-slate-200 bg-slate-50" data-id="element-576">
                <th className="text-left px-4 py-3 font-semibold text-slate-600" data-id="element-577">
                  Product
                </th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600 hidden md:table-cell" data-id="element-578">
                  Category
                </th>
                <th className="text-right px-4 py-3 font-semibold text-slate-600" data-id="element-579">
                  MRP
                </th>
                <th className="text-right px-4 py-3 font-semibold text-slate-600" data-id="element-580">
                  Price
                </th>
                <th className="text-right px-4 py-3 font-semibold text-slate-600 hidden sm:table-cell" data-id="element-581">
                  Stock
                </th>
                <th className="text-right px-4 py-3 font-semibold text-slate-600" data-id="element-582">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody data-id="element-583">
              {filtered.map(product => <tr key={product.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors" data-id="element-584">
                  <td className="px-4 py-3" data-id="element-585">
                    <div className="flex items-center gap-3" data-id="element-586">
                      <img src={product.images[0]} alt={product.name} className="w-10 h-12 object-cover rounded bg-slate-100" data-id="element-587" />
                      <span className="font-medium text-slate-900 line-clamp-1" data-id="element-588">
                        {product.name}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600 hidden md:table-cell" data-id="element-589">
                    {product.category}
                  </td>
                  <td className="px-4 py-3 text-right text-slate-400 line-through" data-id="element-590">
                    ₹{product.mrp}
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-slate-900" data-id="element-591">
                    ₹{product.sellingPrice}
                  </td>
                  <td className="px-4 py-3 text-right hidden sm:table-cell" data-id="element-592">
                    <span className={`font-medium ${getTotalStock(product) <= 10 ? 'text-orange-600' : 'text-green-600'}`} data-id="element-593">
                      {getTotalStock(product)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right" data-id="element-594">
                    <div className="flex items-center justify-end gap-1" data-id="element-595">
                      <button onClick={() => openEditForm(product)} className="p-2 text-slate-400 hover:text-sky-600 hover:bg-sky-50 rounded-lg transition-colors" title="Edit" data-id="element-596">
                        <PencilIcon className="w-4 h-4" data-id="element-597" />
                      </button>
                      <button onClick={() => setDeleteConfirm(product.id)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete" data-id="element-598">
                        <TrashIcon className="w-4 h-4" data-id="element-599" />
                      </button>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && <div className="text-center py-12 text-slate-400" data-id="element-600">
            No products found
          </div>}
      </div>

      {/* Add/Edit Modal */}
      {showForm && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-601">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowForm(false)} data-id="element-602" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-id="element-603">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10" data-id="element-604">
              <h2 className="font-bold text-lg text-slate-900" data-id="element-605">
                {editingId ? 'Edit Product' : 'Add New Product'}
              </h2>
              <button onClick={() => setShowForm(false)} className="p-2 text-slate-400 hover:bg-slate-100 rounded-lg" data-id="element-606">
                <XIcon className="w-5 h-5" data-id="element-607" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4" data-id="element-608">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-609">
                <div className="sm:col-span-2" data-id="element-610">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-611">
                    Product Name *
                  </label>
                  <input type="text" required value={form.name} onChange={e => setForm({
                ...form,
                name: e.target.value
              })} className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" data-id="element-612" />
                </div>
                <div className="sm:col-span-2" data-id="element-613">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-614">
                    Description
                  </label>
                  <textarea value={form.description} onChange={e => setForm({
                ...form,
                description: e.target.value
              })} rows={3} className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 resize-none" data-id="element-615" />
                </div>
                <div data-id="element-616">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-617">
                    Category *
                  </label>
                  <select value={form.category} onChange={e => setForm({
                ...form,
                category: e.target.value
              })} className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" data-id="element-618">
                    {CATEGORIES.map(c => <option key={c} value={c} data-id="element-619">
                        {c}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-620">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-621">
                    Subcategory
                  </label>
                  <input type="text" value={form.subcategory} onChange={e => setForm({
                ...form,
                subcategory: e.target.value
              })} className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" placeholder="e.g. Casual, Formal" data-id="element-622" />
                </div>
                <div className="sm:col-span-2" data-id="element-623">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-624">
                    Image URLs (comma-separated)
                  </label>
                  <div className="relative" data-id="element-625">
                    <ImageIcon className="absolute left-3 top-3 w-4 h-4 text-slate-400" data-id="element-626" />
                    <input type="text" value={form.images} onChange={e => setForm({
                  ...form,
                  images: e.target.value
                })} className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" placeholder="https://example.com/img1.jpg, https://..." data-id="element-627" />
                  </div>
                </div>
                <div data-id="element-628">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-629">
                    MRP (₹) *
                  </label>
                  <input type="number" required value={form.mrp} onChange={e => setForm({
                ...form,
                mrp: e.target.value
              })} className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" data-id="element-630" />
                </div>
                <div data-id="element-631">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-632">
                    Selling Price (₹) *
                  </label>
                  <input type="number" required value={form.sellingPrice} onChange={e => setForm({
                ...form,
                sellingPrice: e.target.value
              })} className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" data-id="element-633" />
                </div>
              </div>

              {/* Size Stock */}
              <div data-id="element-634">
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-635">
                  Stock per Size
                </label>
                <div className="grid grid-cols-5 gap-3" data-id="element-636">
                  {SIZES.map(size => <div key={size} data-id="element-637">
                      <label className="block text-xs text-slate-500 text-center mb-1" data-id="element-638">
                        {size}
                      </label>
                      <input type="number" min="0" value={form.sizes[size]} onChange={e => setForm({
                  ...form,
                  sizes: {
                    ...form.sizes,
                    [size]: e.target.value
                  }
                })} className="w-full px-2 py-2 border border-slate-200 rounded-lg text-sm text-center focus:outline-none focus:ring-2 focus:ring-sky-500" data-id="element-639" />
                    </div>)}
                </div>
              </div>

              <div data-id="element-640">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-641">
                  Tags (comma-separated)
                </label>
                <input type="text" value={form.tags} onChange={e => setForm({
              ...form,
              tags: e.target.value
            })} className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" placeholder="cotton, casual, summer" data-id="element-642" />
              </div>

              <div className="flex gap-6" data-id="element-643">
                <label className="flex items-center gap-2 cursor-pointer" data-id="element-644">
                  <input type="checkbox" checked={form.isFeatured} onChange={e => setForm({
                ...form,
                isFeatured: e.target.checked
              })} className="w-4 h-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500" data-id="element-645" />
                  <span className="text-sm text-slate-700" data-id="element-646">Featured</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer" data-id="element-647">
                  <input type="checkbox" checked={form.isNewArrival} onChange={e => setForm({
                ...form,
                isNewArrival: e.target.checked
              })} className="w-4 h-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500" data-id="element-648" />
                  <span className="text-sm text-slate-700" data-id="element-649">New Arrival</span>
                </label>
              </div>

              <div className="flex gap-3 pt-4 border-t border-slate-200" data-id="element-650">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 py-3 border border-slate-200 text-slate-700 font-medium rounded-lg hover:bg-slate-50 transition-colors" data-id="element-651">
                  Cancel
                </button>
                <button type="submit" className="flex-1 py-3 bg-sky-600 text-white font-semibold rounded-lg hover:bg-sky-700 transition-colors" data-id="element-652">
                  {editingId ? 'Update Product' : 'Add Product'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>}

      {/* Delete Confirmation */}
      {deleteConfirm && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-653">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDeleteConfirm(null)} data-id="element-654" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="relative bg-white rounded-xl p-6 max-w-sm w-full shadow-xl" data-id="element-655">
            <h3 className="font-bold text-lg text-slate-900 mb-2" data-id="element-656">
              Delete Product?
            </h3>
            <p className="text-sm text-slate-500 mb-6" data-id="element-657">
              This action cannot be undone. The product will be removed from the
              store.
            </p>
            <div className="flex gap-3" data-id="element-658">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 border border-slate-200 text-slate-700 font-medium rounded-lg hover:bg-slate-50" data-id="element-659">
                Cancel
              </button>
              <button onClick={() => {
            deleteProduct(deleteConfirm);
            setDeleteConfirm(null);
          }} className="flex-1 py-2.5 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700" data-id="element-660">
                Delete
              </button>
            </div>
          </motion.div>
        </div>}
    </div>;
}